import React, { useMemo, useState } from 'react';
import { safeUserMessage } from '../utils/errorMessages';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AuthLayout from '../components/auth/AuthLayout';
import PasswordRequirements, {
  PASSWORD_REQUIREMENTS_ID,
} from '../components/auth/PasswordRequirements';
import { checkPassword } from '../utils/passwordRule';
import { useAuthContext } from '../contexts/AuthContext';

const normalizeReturnTo = value => {
  if (!value || !value.startsWith('/')) {
    return '/';
  }
  return value;
};

const SignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuthContext();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const returnTo = useMemo(() => normalizeReturnTo(searchParams.get('returnTo')), [searchParams]);
  const passwordCheck = useMemo(() => checkPassword(password), [password]);

  const handleSubmit = async event => {
    event.preventDefault();
    if (!acceptedTerms) {
      setError('Please accept the Terms of Use to continue.');
      return;
    }
    if (!passwordCheck.ok) {
      setError('Please choose a password that meets all the requirements below.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await register({
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        email: email.trim(),
        password,
      });
      navigate(returnTo, { replace: true });
    } catch (signupError) {
      setError(safeUserMessage(signupError, 'Unable to create account. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout title="Create Account" subtitle="Join BKFC and unlock premium content.">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input
            type="text"
            placeholder="First name (optional)"
            value={firstName}
            onChange={event => setFirstName(event.target.value)}
            className="w-full rounded-lg bg-secondary border border-gray-border px-4 py-3 text-white placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/60 focus:border-accent"
          />
          <input
            type="text"
            placeholder="Last name (optional)"
            value={lastName}
            onChange={event => setLastName(event.target.value)}
            className="w-full rounded-lg bg-secondary border border-gray-border px-4 py-3 text-white placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/60 focus:border-accent"
          />
        </div>

        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={event => setEmail(event.target.value)}
          autoComplete="email"
          required
          className="w-full rounded-lg bg-secondary border border-gray-border px-4 py-3 text-white placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/60 focus:border-accent"
        />

        <div>
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={event => setPassword(event.target.value)}
            autoComplete="new-password"
            required
            minLength={8}
            aria-describedby={PASSWORD_REQUIREMENTS_ID}
            className="w-full rounded-lg bg-secondary border border-gray-border px-4 py-3 text-white placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/60 focus:border-accent"
          />
          <div className="mt-2">
            <PasswordRequirements password={password} />
          </div>
        </div>

        <label className="flex items-start gap-3 text-sm text-text-muted">
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={event => setAcceptedTerms(event.target.checked)}
            className="mt-1 accent-accent"
          />
          <span>
            I agree to the{' '}
            <a
              href="https://www.bkfc.com/terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent hover:text-accent-hover"
            >
              Terms of Use
            </a>{' '}
            and{' '}
            <a
              href="https://www.bkfc.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent hover:text-accent-hover"
            >
              Privacy Policy
            </a>
            .
          </span>
        </label>

        {error && (
          <div className="text-sm text-red-400 bg-red-950/40 border border-red-900 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !passwordCheck.ok}
          className="w-full rounded-lg bg-accent text-primary hover:bg-accent-hover disabled:opacity-60 disabled:cursor-not-allowed px-4 py-3 font-semibold transition-colors"
        >
          {submitting ? 'Creating account...' : 'Create Account'}
        </button>
      </form>

      <p className="mt-6 text-sm text-text-muted">
        Already have an account?{' '}
        <Link
          to={`/login?returnTo=${encodeURIComponent(returnTo)}`}
          className="text-accent hover:text-accent-hover"
        >
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
};

export default SignupPage;
